import { useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  useAddBookToList,
  useRemoveBookFromList,
  useBookLists,
  useLists,
  listKeys,
} from '../../hooks/useLists';
import { useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../store/toastStore';
import { useModalStore } from '../../store/modalStore';
import type { Book } from '../../types/models';

interface AddToCollectionModalData {
  book: Book;
}

export function AddToCollectionModal() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const addMutation = useAddBookToList();
  const removeMutation = useRemoveBookFromList();
  const addToast = useToastStore((state) => state.addToast);
  const { modalType, modalData, closeModal, onSuccess } = useModalStore();
  const queryClient = useQueryClient();

  const isOpen = modalType === 'addToCollection';
  const data = (modalData as AddToCollectionModalData) || { book: null };
  const bookId = data.book?.id ?? 0;

  const { data: allLists = [] } = useLists();
  const { data: existingLists = [], isLoading: loadingMembership } = useBookLists(bookId);
  const existingIds = useMemo(() => existingLists.map((l) => l.id), [existingLists]);

  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current || loadingMembership) return;
    hasInitializedRef.current = true;
    setSelectedIds(new Set(existingIds));
  }, [isOpen, existingIds, loadingMembership]);

  const filteredLists = useMemo(() => {
    if (allLists.length === 0) return [];
    if (!searchQuery.trim()) return allLists;
    const query = searchQuery.toLowerCase();
    return allLists.filter(
      (list) =>
        list.name.toLowerCase().includes(query) ||
        list.description?.toLowerCase().includes(query)
    );
  }, [allLists, searchQuery]);

  const toggle = (listId: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId);
      else next.add(listId);
      return next;
    });
  };

  const handleSave = async (): Promise<void> => {
    if (!data.book) return;

    const current = new Set(existingIds);
    const target = selectedIds;

    const toAdd: number[] = [];
    const toRemove: number[] = [];
    for (const id of target) {
      if (!current.has(id)) toAdd.push(id);
    }
    for (const id of current) {
      if (!target.has(id)) toRemove.push(id);
    }

    if (toAdd.length === 0 && toRemove.length === 0) {
      closeModal();
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        ...toAdd.map((listId) => addMutation.mutateAsync({ listId, bookId: data.book.id })),
        ...toRemove.map((listId) =>
          removeMutation.mutateAsync({ listId, bookId: data.book.id })
        ),
      ]);
      queryClient.invalidateQueries({ queryKey: listKeys.forBook(data.book.id) });
      const parts: string[] = [];
      if (toAdd.length) parts.push(`added to ${toAdd.length}`);
      if (toRemove.length) parts.push(`removed from ${toRemove.length}`);
      addToast(`Collections ${parts.join(', ')}`, 'success');
      onSuccess?.();
      closeModal();
    } catch {
      addToast('Failed to update collections', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const existingSet = new Set(existingIds);
  const changeCount =
    Array.from(selectedIds).filter((id) => !existingSet.has(id)).length +
    existingIds.filter((id) => !selectedIds.has(id)).length;

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content add-to-collection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Collections</h2>
          <button onClick={closeModal} className="btn-close" type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-form">
            <div className="form-group">
              <label htmlFor="search">Search Collections</label>
              <input
                id="search"
                type="text"
                placeholder="Type to filter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={saving}
                autoFocus
              />
            </div>
          </div>

          {loadingMembership && (
            <div className="empty-state">Loading current collections...</div>
          )}

          <div className="collection-list">
            {filteredLists.length === 0 ? (
              <div className="empty-state">
                {searchQuery ? 'No collections found' : 'No collections available'}
              </div>
            ) : (
              filteredLists.map((list) => {
                const checked = selectedIds.has(list.id);
                const alreadyIn = existingSet.has(list.id);
                return (
                  <label
                    key={list.id}
                    className={`collection-item collection-item-selectable ${checked ? 'is-selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving || loadingMembership}
                      onChange={() => toggle(list.id)}
                      className="collection-item-checkbox"
                    />
                    <div className="collection-item-content">
                      <div className="collection-item-name">
                        {list.name}
                        {alreadyIn && (
                          <span className="collection-item-badge" title="Book is in this collection">
                            <Check className="icon icon-sm" aria-hidden="true" />
                            In collection
                          </span>
                        )}
                      </div>
                      {list.description && (
                        <div className="collection-item-description">{list.description}</div>
                      )}
                      <div className="collection-item-meta">
                        {list.bookCount} {list.bookCount === 1 ? 'book' : 'books'}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={closeModal} className="btn btn-secondary" type="button" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            className="btn btn-primary"
            type="button"
            disabled={saving || loadingMembership || changeCount === 0}
          >
            {loadingMembership ? 'Loading...' : saving ? 'Saving...' : changeCount > 0 ? `Save (${changeCount} change${changeCount === 1 ? '' : 's'})` : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
